import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiQuery } from '../useApiQuery';
import { useApiMutation } from '../useApiMutation';
import { apiGet, apiPatch, apiPut, operationsClient } from '@/lib/api';
import { contactEndpoints } from '@/lib/api/endpoints';
import { contactKeys } from '@/lib/queryKeys';
import type { TaxExemptReason } from '@/types/contact';

/**
 * One non-blocking warning attached to a contact's exemption status by the
 * backend (`CustomerExemptionService.buildWarnings`) — e.g. a missing
 * certificate number or an expired certificate.
 */
export interface TaxExemptionWarning {
  code: string;
  message: string;
  expiredOn?: string;
}

/** Shape returned by `GET /contacts/:id/tax-exemption`. */
export interface TaxExemptionStatus {
  contactId: string;
  isTaxExempt: boolean;
  taxExemptCertificate: string | null;
  taxExemptReason: TaxExemptReason | string | null;
  taxExemptNotes: string | null;
  /** ISO date string, or null. */
  taxExemptExpiry: string | null;
  /** Sticky tax pin — the group every future document resolves from. */
  taxGroupId: string | null;
  taxGroupPinReason: string | null;
  warnings: TaxExemptionWarning[];
}

/** Body for `PUT /contacts/:id/tax-exemption`. */
export interface UpdateTaxExemptionDto {
  isTaxExempt: boolean;
  taxExemptCertificate?: string | null;
  taxExemptReason?: TaxExemptReason | null;
  taxExemptNotes?: string | null;
  /** ISO date string, or null to clear. */
  taxExemptExpiry?: string | null;
}

function taxExemptionStatusKey(contactId: string) {
  return [...contactKeys.detail(contactId), 'tax-exemption'] as const;
}

/**
 * Customer-level tax exemption — wraps the dedicated exemption endpoints.
 *
 * Returns the current status query plus an update mutation. The mutation
 * invalidates the contact detail + list queries (so badges / summaries
 * refresh) and the status query itself.
 */
export function useCustomerTaxExemption(contactId: string) {
  const queryClient = useQueryClient();

  const statusQuery = useApiQuery<TaxExemptionStatus>({
    queryKey: taxExemptionStatusKey(contactId),
    queryFn: () =>
      apiGet<TaxExemptionStatus>(operationsClient, contactEndpoints.taxExemption(contactId)),
    enabled: Boolean(contactId),
    showErrorToast: false,
  });

  const updateMutation = useApiMutation<TaxExemptionStatus, UpdateTaxExemptionDto>({
    mutationFn: (dto) =>
      apiPut<TaxExemptionStatus>(operationsClient, contactEndpoints.taxExemption(contactId), dto),
    onSuccess: (status) => {
      queryClient.setQueryData(taxExemptionStatusKey(contactId), status);
      void queryClient.invalidateQueries({ queryKey: taxExemptionStatusKey(contactId) });
      void queryClient.invalidateQueries({ queryKey: contactKeys.detail(contactId) });
      void queryClient.invalidateQueries({ queryKey: contactKeys.lists() });
      toast.success('Tax exemption updated');
    },
  });

  return { statusQuery, updateMutation };
}

/**
 * Sticky tax pin — `PATCH /contacts/:id/tax-group`. Pins every FUTURE
 * document for this contact to a tax group (resolvedFrom `CONTACT`), or
 * clears the pin back to geographic resolution. Already-priced documents
 * keep their frozen tax.
 */
export function useSetContactTaxPin(contactId: string) {
  const queryClient = useQueryClient();
  return useApiMutation<
    { contactId: string; taxGroupId: string | null; taxGroupPinReason: string | null },
    { taxGroupId: string | null; reason?: string }
  >({
    mutationFn: (body) => apiPatch(operationsClient, `/contacts/${contactId}/tax-group`, body),
    onSuccess: (res) => {
      toast.success(
        res.taxGroupId
          ? 'Tax group pinned — future documents for this customer use it.'
          : 'Tax pin cleared — future documents resolve from the address.',
      );
      void queryClient.invalidateQueries({ queryKey: taxExemptionStatusKey(contactId) });
      void queryClient.invalidateQueries({ queryKey: contactKeys.detail(contactId) });
    },
  });
}
